import * as _ from 'lodash';
import { Alert } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../../shared/subscriptions/utils';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { usePlanSelectorContext } from '../../../../contexts/PlanSelectorContext';
import { window } from '../../../../globals';
import { getAuthorizeServiceURL } from '../../../../helpers/services';
import Button from '../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import NavigationButton from '../../../generic/Button/NavigationButton';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import Icon, { icons } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link/DocsLink';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import type { SlackProfileResponse } from '../../../../../shared/model-typings';

import useSlackSettings from './useSlackSettings';
import WebhookRow from './WebhookRow';

type SettingsBodyProps = {
  notifications: SlackProfileResponse[] | null,
  onDisconnect: (channel: string) => void;
};

const SLACK_DOCS_LINK = `${DOCS_URL}/slack`;

const AddWebhookButton: VoidFunctionComponent = () => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  if (isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  })) {
    return (
      <Button
        onClick={() => {
          showPlanSelectorModal({ focusFeature: 'slackIntegrationFeature' });
        }}
        type={BUTTON_TYPE.SECONDARY}
      >
        <Icon icon={icons.lockOutline} /> Add another Webhook
      </Button>
    );
  }

  return (
    <NavigationButton
      openInNewTab
      type={BUTTON_TYPE.SECONDARY}
      to={getAuthorizeServiceURL({
        vcsHost: 'slack',
        location: window.location.href,
        serviceConnect: true,
      })}>
      <Icon icon={icons.add} /> Add another Webhook
    </NavigationButton>
  );
};

const SlackSettingsBody: VoidFunctionComponent<SettingsBodyProps> = ({
  notifications,
  onDisconnect,
}) => {
  if (!notifications || _.isEmpty(notifications)) {
    return (
      <h4>You are not connected to Slack</h4>
    );
  }

  return (
    <>
      {notifications.map(notification =>
        <WebhookRow
          key={notification.channel}
          onDisconnect={() => onDisconnect(notification.channel)}
          notification={notification}
        />)}
    </>
  );
};

/**
 * Settings section for the custom license scanning feature.
 */
const SlackSettings: VoidFunctionComponent = () => {
  const { slackSettings, isLoading, errors, onDisconnect } = useSlackSettings();

  if (!window.hasPermission({
    resourceType: ResourceType.OrganizationIntegrationSettings,
    action: Action.Edit,
  })
  ) {
    return (
      <Alert bsStyle="info">
        You are not permitted to edit Slack settings.
      </Alert>
    );
  }

  return (
    <>
      <Header heading="Slack Settings" />
      <Section>
        <Section.Left heading="Slack Settings">
          <Section.Paragraph>
            Configure your Slack notification settings
          </Section.Paragraph>
          <NavigationButton
            to={SLACK_DOCS_LINK}
            size="medium"
            type="secondary"
            openInNewTab
          >
            View documentation
          </NavigationButton>
        </Section.Left>
        <Section.Right>
          {errors && (
            <ErrorAlert status={{ errors }} />
          )}
          {isLoading
            ? <Loader />
            : <SlackSettingsBody notifications={slackSettings} onDisconnect={onDisconnect} />}
          <div className="margin-top-lg">
            <AddWebhookButton />
          </div>
        </Section.Right>
      </Section>
    </>
  );
};

export default SlackSettings;
